/**
 * Fog-of-war overlay for the map. One translucent tile-sized sprite per map
 * cell; per frame we set each sprite's alpha + tint based on the player's
 * FovMemory:
 *   - currently visible: alpha 0 (world shows through)
 *   - seen before, not currently visible: gray alpha ≈ 0.55 (desaturated)
 *   - never seen: near-black alpha ≈ 0.97 (hidden)
 *
 * See CLAUDE.md architecture rule 3 (glyph atlas — no DOM text, no per-frame
 * fillText). This layer is pure Sprite tinting.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import type { FovMemory } from '../sim/fovMemory';
import { tileAt, type MapData } from '../sim/map';
import { TileType } from '../sim/tiles';

/** Very dark near-black for BOTH memory tiles and unseen tiles. The
 *  difference is opacity — memory tiles let *some* underlying color through
 *  so a wall stays a wall, just dim, while unseen tiles are almost fully
 *  masked. Nothing is gray/pale — memory reads as darker than lit rooms. */
const FOG_SEEN_TINT = 0x080a0e;
const FOG_UNSEEN_TINT = 0x03040a;
const ALPHA_VISIBLE = 0;
const ALPHA_SEEN = 0.78;
const ALPHA_UNSEEN = 0.99;
/** A tile in the sound field brighter than this is unfogged so its sound-hue
 *  tint shows at full strength regardless of the vision cone. */
const SOUND_REVEAL_MIN = 0.03;
/** Same idea for guard vision cones. In normal play the cone field is already
 *  clipped to the player's FOV (so these tiles are visible anyway); this reveal
 *  matters for the debug `showAll` field, which un-fogs the whole floor's cones
 *  so the debug quad overlay reads across areas the player can't currently see. */
const VISION_REVEAL_MIN = 0.03;

export class FogLayer {
  readonly container: Container;
  private sprites: Sprite[] = [];
  private width = 0;
  private height = 0;
  private tileSize = 0;

  constructor() {
    this.container = new Container();
  }

  /** Build one fog sprite per non-Void tile. Call once after `loadMap`. */
  build(map: MapData, tileSize: number): void {
    this.container.removeChildren();
    this.sprites.length = 0;
    this.width = map.width;
    this.height = map.height;
    this.tileSize = tileSize;
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const s = new Sprite(Texture.WHITE);
        s.setSize(tileSize, tileSize);
        s.x = x * tileSize;
        s.y = y * tileSize;
        // Void tiles get an always-opaque black cover so the game area
        // reads as a clean silhouette.
        if (tileAt(map, x, y) === TileType.Void) {
          s.tint = FOG_UNSEEN_TINT;
          s.alpha = 1;
        } else {
          s.tint = FOG_UNSEEN_TINT;
          s.alpha = ALPHA_UNSEEN;
        }
        this.container.addChild(s);
        this.sprites.push(s);
      }
    }
  }

  /**
   * Per-frame update. Assumes `fov.currentlyVisible` matches this build.
   *
   * `soundTiles` (optional) is the current sound field. Any tile carrying an
   * audible sound is UNFOGGED (alpha 0) regardless of the vision cone, so the
   * sound-hue tint on it reads at full brightness inside and outside the FOV —
   * "sound is heard everywhere, not just where you're looking".
   */
  update(
    fov: FovMemory,
    map: MapData,
    soundTiles?: ReadonlyMap<number, { intensity: number }>,
    visionTiles?: ReadonlyMap<number, { intensity: number }>,
  ): void {
    for (let idx = 0; idx < this.sprites.length; idx++) {
      const s = this.sprites[idx]!;
      if (tileAt(map, idx % map.width, Math.floor(idx / map.width)) === TileType.Void) continue;
      const sound = soundTiles?.get(idx);
      const vision = visionTiles?.get(idx);
      if (
        (sound && sound.intensity > SOUND_REVEAL_MIN) ||
        (vision && vision.intensity > VISION_REVEAL_MIN)
      ) {
        // Sound / vision-cone reveal wins over fog so the tinted tile is lit.
        s.tint = FOG_SEEN_TINT;
        s.alpha = ALPHA_VISIBLE;
      } else if (fov.isCurrentlyVisible(idx)) {
        s.tint = FOG_SEEN_TINT;
        s.alpha = ALPHA_VISIBLE;
      } else if (fov.isSeen(idx)) {
        s.tint = FOG_SEEN_TINT;
        s.alpha = ALPHA_SEEN;
      } else {
        s.tint = FOG_UNSEEN_TINT;
        s.alpha = ALPHA_UNSEEN;
      }
    }
    // Consume width/height so TS doesn't complain about unused state.
    void this.width; void this.height; void this.tileSize;
  }
}
